// Shared "grainy paper" texture utilities for deck-statistics charts.
//
// Two consumers need texture, and they live in different rendering
// worlds, so we expose both:
//
//   1. <ChartNoiseDefs />. An invisible SVG containing an feTurbulence
//                             filter. Any SVG <rect> / <path> in the
//                             document can reference it via
//                             `filter="url(#vk-chart-noise)"`. Used by
//                             the Recharts Bar shape components.
//
//   2. noiseBackgroundLayer(color). Returns a `background` value that
//                             overlays the existing tex-noise.webp
//                             texture on top of a solid colour or
//                             gradient, blended with `multiply` so the
//                             bar's hue is preserved but each bar picks
//                             up the paper-like grain of the rest of
//                             the app. Used by the pure-CSS rounded bars
//                             in BreakdownsPanel / RarityPanel / AtlasPanel.
//
// Textures are deliberately subtle so the numbers stay readable. The
// filter's noise alpha is ~0.18; the CSS overlay blends at ~28 %
// opacity. Both can be tuned here without touching consumers.

export const CHART_NOISE_FILTER_ID = 'vk-chart-noise';
export const CHART_NOISE_SVG_URL = `url(#${CHART_NOISE_FILTER_ID})`;

/**
 * Renders a hidden SVG that defines the `vk-chart-noise` filter. Mount
 * once at the top of the deck stats overlay; Recharts' own SVG roots
 * can then reference the filter by id.
 */
export function ChartNoiseDefs() {
  return (
    <svg
      aria-hidden="true"
      style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}
    >
      <defs>
        <filter id={CHART_NOISE_FILTER_ID} x="0" y="0" width="100%" height="100%">
          {/* Fractal noise produces a soft, paper-like grain. Higher
              baseFrequency = tighter speckle; stitchTiles keeps the
              pattern continuous across tiling. */}
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves="2"
            seed="7"
            stitchTiles="stitch"
            result="noise"
          />
          {/* Centre the noise around mid-grey and scale its alpha down
              so some specks brighten and others darken when we blend
              with overlay below. Without the alpha clamp the grain
              overwhelms the bar colour. */}
          <feColorMatrix
            in="noise"
            type="matrix"
            values="
              0 0 0 0 0.5
              0 0 0 0 0.5
              0 0 0 0 0.5
              0 0 0 0.7 -0.35"
            result="grain"
          />
          {/* Clip the noise to the bar silhouette so we don't paint
              grain outside the bar bounds. */}
          <feComposite in="grain" in2="SourceGraphic" operator="in" result="grainClipped" />
          {/* Overlay blend keeps the bar's hue (darks stay dark, lights
              stay light) while adding texture through luminance shifts. */}
          <feBlend in="SourceGraphic" in2="grainClipped" mode="overlay" />
        </filter>
      </defs>
    </svg>
  );
}

/**
 * Wraps a CSS `background` declaration with an overlay of the shared
 * tex-noise.webp texture. Use as the `background` style value on any
 * DOM bar so it picks up the same grain as the SVG charts.
 *
 * Example:
 *   style={{
 *     background: noiseBackgroundLayer(
 *       `linear-gradient(180deg, ${color} 0%, ${color}cc 100%)`,
 *     ),
 *     backgroundBlendMode: 'overlay, normal',
 *   }}
 */
export function noiseBackgroundLayer(base) {
  return `url('/tex-noise.webp'), ${base}`;
}

/**
 * Full style object convenience for CSS bars. Saves every caller
 * from repeating the blend-mode + size settings.
 */
export function noiseBarStyle(baseBackground, extras = {}) {
  return {
    background: noiseBackgroundLayer(baseBackground),
    backgroundSize: '120px 120px, auto',
    backgroundRepeat: 'repeat, no-repeat',
    backgroundBlendMode: 'overlay, normal',
    ...extras,
  };
}
